import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Typography, Button, Grid, Chip, Divider, IconButton, Card, CardContent, Alert, Snackbar, CircularProgress, Table, TableBody, TableRow, TableCell
} from '@mui/material';
import {ArrowBack,Edit,Verified,Block,Phone,Email,LocationOn,Business,AccountBalance,Description,Star,CheckCircle,Cancel
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiRequest } from '../../fetchSetup';


const DistributorDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const DRAWER_WIDTH = '280px';

  const [distributor, setDistributor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchDistributor();
  }, [id]);

  const fetchDistributor = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(
        `${process.env.REACT_APP_API}/distributors/${id}`
      );
      const data = await response.json();
      
      if (response.ok) {
        setDistributor(data);
      } else {
        showSnackbar('Failed to load distributor', 'error');
      }
    } catch (error) {
      console.error('Error fetching distributor:', error);
      showSnackbar('Error loading distributor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!distributor) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', ml: { xs: 0, md: DRAWER_WIDTH } }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">Distributor not found</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', ml: { xs: 0, md: DRAWER_WIDTH } }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/DistributorList')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A' }}>
                {distributor.name}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {distributor.distributor_code}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/distributors/edit/${id}`)}
            sx={{
              bgcolor: '#006D77',
              '&:hover': { bgcolor: '#005962' }
            }}
          >
            Edit
          </Button>
        </Box>

        {/* Status Chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Chip
            label={distributor.is_active ? 'Active' : 'Inactive'}
            color={distributor.is_active ? 'success' : 'default'}
            icon={distributor.is_active ? <CheckCircle /> : <Cancel />}
          />
          {distributor.is_verified && (
            <Chip
              label="Verified"
              color="info"
              icon={<Verified />}
            />
          )}
          {distributor.blacklisted && (
            <Chip
              label="Blacklisted"
              color="error"
              icon={<Block />}
            />
          )}
          <Chip
            label={distributor.distributor_type}
            variant="outlined"
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Business sx={{ mr: 1 }} />
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
                    <TableCell>{distributor.company_name || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {distributor.distributor_type}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ fontSize: 16, mr: 1, color: '#666' }} />
                        {distributor.phone || 'N/A'}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {distributor.alternate_phone && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Alternate Phone</TableCell>
                      <TableCell>{distributor.alternate_phone}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email sx={{ fontSize: 16, mr: 1, color: '#666' }} />
                        {distributor.email || 'N/A'}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {distributor.website && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Website</TableCell>
                      <TableCell>
                        <a href={distributor.website} target="_blank" rel="noopener noreferrer">
                          {distributor.website}
                        </a>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Address */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <LocationOn sx={{ mr: 1 }} />
                Address
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {distributor.address ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {distributor.address.street}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {distributor.address.city}, {distributor.address.state}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {distributor.address.pincode}
                  </Typography>
                  <Typography variant="body2">
                    {distributor.address.country}
                  </Typography>
                </Box>
              ) : (
                <Typography color="textSecondary">No address provided</Typography>
              )}
            </Paper>
          </Grid>

          {/* Legal & Compliance */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Description sx={{ mr: 1 }} />
                Legal & Compliance
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>GST Number</TableCell>
                    <TableCell>{distributor.gst_number || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>PAN Number</TableCell>
                    <TableCell>{distributor.pan_number || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Drug License</TableCell>
                    <TableCell>{distributor.drug_license_number || 'N/A'}</TableCell>
                  </TableRow>
                  {distributor.drug_license_expiry && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>License Expiry</TableCell>
                      <TableCell>{distributor.drug_license_expiry}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>FSSAI Number</TableCell>
                    <TableCell>{distributor.fssai_number || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Banking Details */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <AccountBalance sx={{ mr: 1 }} />
                Banking Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {distributor.bank_details ? (
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Bank Name</TableCell>
                      <TableCell>{distributor.bank_details.bank_name || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Account Name</TableCell>
                      <TableCell>{distributor.bank_details.account_name || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Account Number</TableCell>
                      <TableCell>{distributor.bank_details.account_number || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>IFSC Code</TableCell>
                      <TableCell>{distributor.bank_details.ifsc_code || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                      <TableCell>{distributor.bank_details.branch || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>UPI ID</TableCell>
                      <TableCell>{distributor.bank_details.upi_id || 'N/A'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <Typography color="textSecondary">No banking details provided</Typography>
              )}
            </Paper>
          </Grid>

          {/* Business Terms */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Business Terms
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Payment Terms</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {distributor.payment_terms?.replace(/_/g, ' ') || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Credit Limit</TableCell>
                    <TableCell>₹{distributor.credit_limit?.toLocaleString() || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Credit Period</TableCell>
                    <TableCell>{distributor.credit_period_days || 0} days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                    <TableCell>{distributor.discount_percentage || 0}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Min Order Value</TableCell>
                    <TableCell>₹{distributor.minimum_order_value?.toLocaleString() || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Delivery Days</TableCell>
                    <TableCell>{distributor.delivery_days || 0} days</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Rating */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Star sx={{ mr: 1, color: '#ffc107' }} />
                Performance Rating
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {distributor.rating ? (
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Overall Rating</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {distributor.rating.overall_rating?.toFixed(1) || '5.0'}
                          <Star sx={{ fontSize: 16, ml: 0.5, color: '#ffc107' }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Delivery Rating</TableCell>
                      <TableCell>{distributor.rating.delivery_rating?.toFixed(1) || '5.0'}/5</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Quality Rating</TableCell>
                      <TableCell>{distributor.rating.quality_rating?.toFixed(1) || '5.0'}/5</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Total Orders</TableCell>
                      <TableCell>{distributor.rating.total_orders || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>On-Time Deliveries</TableCell>
                      <TableCell>{distributor.rating.on_time_deliveries || 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <Typography color="textSecondary">No rating data available</Typography>
              )}
            </Paper>
          </Grid>

          {/* Contact Persons */}
          {distributor.contact_persons && distributor.contact_persons.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Contact Persons
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {distributor.contact_persons.map((contact, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {contact.name}
                            {contact.is_primary && (
                              <Chip
                                label="Primary"
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            {contact.designation}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Phone sx={{ fontSize: 14, mr: 1 }} />
                            {contact.phone}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Email sx={{ fontSize: 14, mr: 1 }} />
                            {contact.email}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Categories and Brands */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Product Categories
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {distributor.product_categories && distributor.product_categories.length > 0 ? (
                  distributor.product_categories.map((category, index) => (
                    <Chip key={index} label={category} color="primary" variant="outlined" />
                  ))
                ) : (
                  <Typography color="textSecondary">No categories specified</Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Brands Supplied
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {distributor.brands_supplied && distributor.brands_supplied.length > 0 ? (
                  distributor.brands_supplied.map((brand, index) => (
                    <Chip key={index} label={brand} color="secondary" variant="outlined" />
                  ))
                ) : (
                  <Typography color="textSecondary">No brands specified</Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Notes */}
          {distributor.notes && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {distributor.notes}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Terms & Conditions */}
          {distributor.terms_conditions && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Terms & Conditions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {distributor.terms_conditions}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DistributorDetails;